import * as yup from 'yup';
import { AnySchema } from 'yup';
import { ItemScheme } from '../../../Components/Form/MagicFormItem';
import { MagicField } from '../../../Enums/MagicField.enum';
import { Rule } from '../Enums/Rule.enum';
import { RequiredRule } from '../Interfaces/RequiredRule.interface';
import { RequiredWhenAnyInArrayRule } from '../Interfaces/RequiredWhenAnyInArrayRule.interface';
import { NCZIReferenceValidator } from '../../Nczi/Components/ReferenceSearchSelect/NCZIReferenceSearchSelect';

const nonKeyWhiteList = ['condition', 'hstack', 'container', 'collapse'];

interface InputValidations {
	[MagicField.PRICE_WITH_CURRENCY]: (
		inputName: string,
		isRequired?: boolean,
		allowZero?: boolean,
	) => yup.AnySchema;
}

const isValueEmpty = (val: any): boolean =>
	val == null ||
	(typeof val === 'string' && val.trim() === '') ||
	(Array.isArray(val) && val.length === 0) ||
	val === false;

const rulesMatch = (
	rules: RequiredRule[],
	parent: Record<string, any>,
): boolean =>
	rules.every(r => {
		const dep = parent?.[r.formControlKey];
		const cmp = r.compareKey ? dep?.[r.compareKey] : dep?.value ?? dep;
		const rule = r.rule as any;
		if (rule === Rule.EQUAL || rule === 'equal') {
			return cmp === r.value;
		}
		if (rule === Rule.NOT_EQUAL || rule === 'notEqual') {
			return cmp !== r.value;
		}
		return cmp !== r.value;
	});

export const generateDynamicRequired = <T extends yup.AnySchema>(
	schema: T,
	rules: RequiredRule[],
	message: string,
): T => {
	if (!rules?.length) return schema;
	const hasGroupRule = rules.some(
		r => Array.isArray(r.groupKeys) && r.groupKeys.length > 0,
	);
	if (hasGroupRule) {
		return schema.test('dynamic-required-group', message, function () {
			const parent = this.parent as Record<string, any>;
			if (!rulesMatch(rules, parent)) {
				return true;
			}
			const groupRule = rules.find(
				r => Array.isArray(r.groupKeys) && r.groupKeys.length > 0,
			);
			if (!groupRule || !groupRule.groupKeys?.length) {
				return true;
			}
			const isGroupEmpty = groupRule.groupKeys.every(key =>
				isValueEmpty(parent?.[key]),
			);
			return !isGroupEmpty;
		}) as T;
	}
	return schema.test('dynamic-required', message, function (val) {
		const parent = this.parent as Record<string, any>;
		if (!rulesMatch(rules, parent)) {
			return true;
		}
		return !isValueEmpty(val);
	}) as T;
};

const isRequiredWhenAnyInArray = (
	array: unknown,
	rule: RequiredWhenAnyInArrayRule,
): boolean => {
	if (!Array.isArray(array)) {
		return false;
	}

	return array.some((row: Record<string, unknown>) => {
		const fieldVal = row?.[rule.field] as
			| { value?: string }
			| string
			| undefined;
		const compareVal =
			fieldVal != null && typeof fieldVal === 'object'
				? fieldVal.value
				: fieldVal;

		return rule.matchingValues.includes(String(compareVal ?? ''));
	});
};

export const generateRequiredWhenAnyInArray = <T extends yup.AnySchema>(
	schema: T,
	rule: RequiredWhenAnyInArrayRule,
	message: string,
): T => {
	return schema.test('required-when-any-in-array', message, function (val) {
		const parent = this.parent as Record<string, unknown>;
		const array = parent?.[rule.arrayKey];

		if (!isRequiredWhenAnyInArray(array, rule)) {
			return true;
		}

		return !isValueEmpty(val);
	}) as T;
};

const addRules = <T extends AnySchema>(
	yupItem: T,
	rules?: { type: string; value: number; message: string }[],
): T => {
	let schema: any = yupItem;
	for (const rule of rules || []) {
		switch (rule.type) {
			case 'maxLength':
			case 'max':
				schema = schema.max(rule.value, rule.message) as T;
				break;
			case 'min':
				schema = schema.min(rule.value, rule.message) as T;
				break;
		}
	}
	return schema as T;
};

const getConditionEnabledSchema = (childSchema: yup.AnySchema) => {
	if (childSchema.type === 'object') {
		return childSchema.clone().default({}).required();
	}
	return childSchema;
};

const getConditionDisabledSchema = (schema: yup.AnySchema) => {
	return schema.nullable();
};

const loopItems = ({
	items,
	returnObject = false,
	inputValidations,
	t,
}: {
	items: ItemScheme[];
	returnObject?: boolean;
	inputValidations: InputValidations;
	t: (key: string) => string | undefined;
}) => {
	let data = {};
	for (const item of items) {
		const { key, type, label, title } = item;
		const fieldName = label || title || '';
		if (!key && nonKeyWhiteList.indexOf(type) === -1) {
			continue;
		}
		if (
			['radio_list', 'checkbox_list', 'select'].indexOf(item.type) === -1 &&
			item?.children?.length
		) {
			if (item.type === 'multiplier') {
				// @ts-ignore
				data[key] = yup.array().of(
					loopItems({
						items: item.children,
						returnObject: false,
						inputValidations,
						t,
					}),
				);
			} else if (item.type === 'condition') {
				const conditionChildren = loopItems({
					items: item.children,
					returnObject: true,
					inputValidations,
					t,
				}) as Record<string, yup.AnySchema | undefined>;
				const parsed: Record<string, yup.AnySchema> = {};
				for (const conditionChildKey in conditionChildren) {
					const childSchema =
						conditionChildren[conditionChildKey] ?? yup.mixed().nullable();
					if (item?.settings?.rules) {
						parsed[conditionChildKey] = yup.mixed();
						for (const rule of item?.settings?.rules ?? []) {
							let formControlKey = rule.formControlKey.split('.');
							formControlKey = formControlKey[formControlKey.length - 1];
							parsed[conditionChildKey] = parsed[conditionChildKey].when(
								formControlKey,
								{
									is: (itemValue: any) => {
										const realValue = itemValue?.value ?? itemValue;
										switch (rule.rule) {
											case 'equal':
												return realValue === rule.value;
											case 'notEqual':
												return realValue !== rule.value;
											case 'in':
												return Array.isArray(rule.value)
													? rule.value.indexOf(realValue) !== -1
													: false;
											default:
												return false;
										}
									},
									then: () => getConditionEnabledSchema(childSchema),
									otherwise: (s: yup.AnySchema) =>
										getConditionDisabledSchema(s),
								},
							);
						}
					} else {
						let formControlKey = item?.value?.toString()?.split('.') || [];
						formControlKey = formControlKey[formControlKey.length - 1];
						parsed[conditionChildKey] = yup.mixed().when(formControlKey, {
							is: (val: any) => {
								const { equal, in: pin } = item?.settings || {};
								if (typeof equal !== 'undefined') {
									return val === equal;
								}
								if (pin) {
									return pin.indexOf(val) !== -1;
								}
								return false;
							},
							then: () => getConditionEnabledSchema(childSchema),
							otherwise: (s: yup.AnySchema) => getConditionDisabledSchema(s),
						});
					}
				}
				data = {
					...data,
					...parsed,
				};
			} else if (item.type === 'hstack' || item.type === 'collapse') {
				const conditionChildren = loopItems({
					items: item.children,
					returnObject: true,
					inputValidations,
					t,
				});
				data = {
					...data,
					...conditionChildren,
				};
			} else if (item.type === 'container' && !key) {
				const containerChildren = loopItems({
					items: item.children,
					returnObject: true,
					inputValidations,
					t,
				});
				data = {
					...data,
					...containerChildren,
				};
			} else {
				data[key] = yup.object().shape(
					loopItems({
						items: item.children,
						returnObject: true,
						inputValidations,
						t,
					}),
				);
				if (item?.settings?.required) {
					data[key] = data[key].required();
				}
			}
		} else {
			let yupItem;
			switch (item.type) {
				case MagicField.PRICE_WITH_CURRENCY:
					yupItem = inputValidations[MagicField.PRICE_WITH_CURRENCY](
						item.title ?? '',
						false,
						item?.settings?.allowZero,
					).nullable();
					break;
				case MagicField.TEXT_FIELD:
					if (item?.subType === 'number') {
						yupItem = yup
							.number()
							.nullable()
							.typeError('validation.form.mustFillNumber');
					} else {
						yupItem = yup.string().nullable();
					}
					yupItem = addRules(yupItem, item.settings?.rules);
					break;
				case MagicField.NUMBER:
					yupItem = yup
						.number()
						.nullable()
						.transform((value, originalValue) => {
							return originalValue === '' ? null : value;
						})
						.typeError('validation.form.mustFillNumber');
					break;
				case MagicField.CONTAINER:
					yupItem = yup.object().nullable().default(undefined);
					break;
				case MagicField.PHONE_NUMBER:
					yupItem = yup.string().nullable();
					break;
				case MagicField.CHECKBOX:
					yupItem = yup.boolean();
					break;
				case MagicField.DATEPICKER:
					yupItem = yup.mixed().nullable();
					break;
				case MagicField.SELECT:
				case MagicField.DOCTOR_SELECT:
				case MagicField.PATIENT_SELECT:
				case MagicField.QUESTIONNAIRE_SELECT:
				case MagicField.ORGANIZATION_UNIT_SELECT:
				case MagicField.VAT_RATE:
				case MagicField.TERM_ITEM_SELECT:
				case MagicField.QUESTIONNAIRE_SCHEDULE_FREQUENCY:
				case MagicField.P1_MEDICAMENT:
				case MagicField.P1_PAYMENT_COVERAGE_TYPE:
				case MagicField.P1_MEDICAL_REQUEST_TYPE:
				case MagicField.P1_MEDICAL_FACILITY:
				case MagicField.P1_DISEASES:
				case MagicField.P1_PROCEDURES:
				case MagicField.DIAGNOSIS_CZECH:
					yupItem = yup.mixed().nullable().default(undefined);
					break;
				case MagicField.RADIOLIST:
					const radioListValues =
						item?.children?.map(child => child?.value) || [];
					if (!item?.settings?.required) {
						yupItem = yup.mixed().nullable().default(null);
					} else {
						yupItem = yup
							.mixed()
							.oneOf(radioListValues, t('validation.form.oneOf'))
							.nullable()
							.default(null);
					}
					break;
				case MagicField.EMAIL:
					yupItem = yup
						.string()
						.email('validation.form.email')
						.typeError('validation.form.mustFillEmail');
					break;
				case MagicField.TEXTAREA:
					yupItem = yup.string().nullable();
					break;
				case MagicField.WORKPLACE_SELECT:
				case MagicField.FIELD_OF_CARE_SUKL_SELECT:
					yupItem = yup
						.object()
						.shape({
							label: yup.string(),
							value: yup.string(),
						})
						.nullable();
					break;
				case MagicField.ROLE_MULTISELECT_WITH_CHECKLIST:
				case MagicField.INSURANCE_COMPANIES_MULTISELECT_WITH_CHECKLIST:
				case MagicField.INSURANCE_COMPANY_BATCH_TYPES_MULTISELECT_WITH_CHECKLIST:
					yupItem = yup
						.array()
						.of(yup.object({ value: yup.string(), label: yup.string() }))
						.nullable();
					break;
				case MagicField.RICH_TEXT_FIELD:
					yupItem = yup
						.object({
							plain: addRules(yup.string(), item.settings?.rules),
							html: yup.string(),
						})
						.nullable();
					break;
				case MagicField.NZCI_VACCINATION_KIND:
				case MagicField.NZCI_VACCINATION_TYPE:
				case MagicField.NZCI_SPECIALIZATION:
				case MagicField.NZCI_STATE:
				case MagicField.NCZI_EXAMINATION_URGENCY:
				case MagicField.NCZI_ALLERGENS:
				case MagicField.NCZI_ATC_CLASSIFICATION_SYSTEM:
				case MagicField.NCZI_IMPLANTED_MEDICAL_DEVICES:
				case MagicField.NCZI_DIAGNOSIS:
				case MagicField.NCZI_RECOMMENDATION_FOR_EXAMINATION:
				case MagicField.NCZI_PROFESSIONAL_FOCUS:
				case MagicField.NCZI_EXAMINED_BODY_PART:
				case MagicField.NCZI_MODALITY_OF_IMAGING_EXAMINATION:
				case MagicField.NCZI_SEARCH_SELECT:
				case MagicField.NCZI_SELECT:
					yupItem = yup.mixed().nullable().default(undefined);
					break;
			}
			if (yupItem) {
				const requiredMessage =
					item.settings?.errorMessage ??
					`${t(fieldName)} ${t('validation.form.isRequiredToFill')}`;
				if (item?.settings?.required) {
					switch (item.type) {
						case MagicField.SELECT:
						case MagicField.DOCTOR_SELECT:
						case MagicField.PATIENT_SELECT:
						case MagicField.QUESTIONNAIRE_SELECT:
						case MagicField.ORGANIZATION_UNIT_SELECT:
						case MagicField.VAT_RATE:
						case MagicField.TERM_ITEM_SELECT:
						case MagicField.WORKPLACE_SELECT:
						case MagicField.FIELD_OF_CARE_SUKL_SELECT:
						case MagicField.P1_MEDICAMENT:
						case MagicField.P1_PAYMENT_COVERAGE_TYPE:
						case MagicField.P1_MEDICAL_REQUEST_TYPE:
						case MagicField.P1_MEDICAL_FACILITY:
						case MagicField.P1_DISEASES:
						case MagicField.P1_PROCEDURES:
						case MagicField.DIAGNOSIS_CZECH:
							yupItem = yupItem
								.clone()
								.test('is-required-select', requiredMessage, value => {
									const realVal =
										typeof value === 'object' ? value?.value : value;
									return realVal !== null && typeof realVal !== 'undefined';
								});
							break;
						case MagicField.ROLE_MULTISELECT_WITH_CHECKLIST:
						case MagicField.INSURANCE_COMPANIES_MULTISELECT_WITH_CHECKLIST:
						case MagicField.INSURANCE_COMPANY_BATCH_TYPES_MULTISELECT_WITH_CHECKLIST:
							yupItem = yupItem
								.clone()
								.test('is-required-select', requiredMessage, value => {
									return (
										Array.isArray(value) &&
										value.length > 0 &&
										value[0]?.value !== undefined &&
										value[0]?.value !== null
									);
								});
							break;
						case MagicField.TEXT_FIELD:
							yupItem = yupItem.clone().required(requiredMessage);
							break;
						case MagicField.RADIOLIST:
							yupItem = yupItem.clone().required(requiredMessage);
							break;
						case MagicField.NZCI_VACCINATION_KIND:
						case MagicField.NZCI_VACCINATION_TYPE:
						case MagicField.NZCI_SPECIALIZATION:
						case MagicField.NZCI_STATE:
						case MagicField.NCZI_EXAMINATION_URGENCY:
						case MagicField.NCZI_ALLERGENS:
						case MagicField.NCZI_ATC_CLASSIFICATION_SYSTEM:
						case MagicField.NCZI_IMPLANTED_MEDICAL_DEVICES:
						case MagicField.NCZI_DIAGNOSIS:
						case MagicField.NCZI_RECOMMENDATION_FOR_EXAMINATION:
						case MagicField.NCZI_PROFESSIONAL_FOCUS:
						case MagicField.NCZI_EXAMINED_BODY_PART:
						case MagicField.NCZI_MODALITY_OF_IMAGING_EXAMINATION:
						case MagicField.NCZI_SEARCH_SELECT:
						case MagicField.NCZI_SELECT:
							yupItem = NCZIReferenceValidator(requiredMessage);
							break;
						default:
							yupItem = yupItem.clone().required(requiredMessage);
							break;
					}
				} else {
					yupItem = yupItem.clone().nullable();
				}
				if (item?.settings?.requiredRules) {
					yupItem = generateDynamicRequired(
						yupItem.clone(),
						item.settings.requiredRules,
						requiredMessage,
					);
				}
				if (item?.settings?.requiredWhenAnyInArray) {
					yupItem = generateRequiredWhenAnyInArray(
						yupItem.clone(),
						item.settings.requiredWhenAnyInArray,
						item.settings?.errorMessage ?? requiredMessage,
					);
				}
			}
			if (key && yupItem) {
				data[key] = yupItem;
			}
		}
	}
	if (returnObject) {
		return data;
	}
	return yup.object(data);
};

export const getValidationScheme = (
	data: ItemScheme[],
	inputValidations: InputValidations,
	t: (key: string) => string | undefined,
) => {
	return loopItems({ items: data, inputValidations, t });
};
